import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Everything the app has to say, in one place.
 *
 * Confirmations used to appear as toasts in the bottom corner and vanish, and longer explanations as
 * a panel wherever the page had room for one. Neither could be looked at twice, so a brewer who
 * walked away missed it (Tyler, 2026-09-21). They now come out of the bell in the header, linger, and
 * stay in the list behind it.
 *
 * And they are kept (Tyler, 2026-09-24). A notification lived only in the tab that raised it, so a
 * reload lost it — and with it the undo on a receipt, which is the only undo the invoice screen still
 * offers. Each one is now written to the brewery's database as it is said, for thirty days: the bell
 * shows the last ten, the Notifications page all of them, and an action can be pressed from either,
 * on any device, until it has been used.
 */

export type Level =
  /** It worked, and the brewer may like to know. */
  | "info"
  /** Something was taken back or set aside, and is worth noticing. */
  | "notice"
  /** Something did not happen, or happened in a way that wants a person. */
  | "warning";

/**
 * What a notification offers to do, as data rather than code: what it does and to what. That is what
 * lets it be kept, and pressed later from somewhere that never saw it raised.
 */
export type NoteActionData = { kind: "undo_receipt"; invoiceId: string };
export type ActionKind = NoteActionData["kind"];

/**
 * What a notification is about, as data like its action, so tapping it opens that page from the
 * bubble, the bell or the Notifications page, on any device (Tyler, 2026-10-01).
 */
export type NoteLinkData =
  | { kind: "invoice"; invoiceId: string }
  | { kind: "item"; itemId: string }
  | { kind: "recipe"; recipeId: string }
  | { kind: "recipe_draft"; draftId: string; recipeId?: string }
  | { kind: "brew"; brewId: string }
  | { kind: "stock_import"; importId: string }
  | { kind: "history" };

/** How an action is run, and what it is called. Supplied to the provider, one per kind. */
export interface ActionRunner {
  label: string;
  running: string;
  done: string;
  /** Does it. Throwing leaves the action there to try again, and says why it did not work. */
  run: (action: NoteActionData) => Promise<unknown>;
  /** What to say once it has worked. */
  after?: NotifyInput;
}
export type ActionRunners = Record<ActionKind, ActionRunner>;

export type ActionState = "ready" | "running" | "done";

export interface Note {
  id: string;
  level: Level;
  title: string;
  body?: string;
  at: number;
  /** Still in its bubble under the bell, rather than only in the list behind it. */
  showing: boolean;
  read: boolean;
  action?: { data: NoteActionData; state: ActionState };
  link?: NoteLinkData;
}

/** A notification as it is kept. */
export interface StoredNote {
  id: string;
  level: Level;
  title: string;
  body: string | null;
  action: NoteActionData | null;
  link: NoteLinkData | null;
  actionDone: boolean;
  read: boolean;
  /** ISO timestamp. */
  at: string;
}

/**
 * Where notifications are kept. The server, in the app; nothing at all by default, which is what a
 * test or a page without a brewery behind it gets. A store that fails leaves the notification on the
 * screen for this session regardless: what the app has to say is never lost to a dropped request.
 */
export interface NoteStore {
  load(): Promise<StoredNote[]>;
  add(note: {
    id: string;
    level: Level;
    title: string;
    body?: string;
    action?: NoteActionData;
    link?: NoteLinkData;
  }): Promise<void>;
  readAll(): Promise<void>;
  actionDone(id: string): Promise<void>;
}

/** How long a bubble stays up before it goes into the bell (Tyler, 2026-09-21). */
export const LINGER_MS = 8000;

/** How many the bell shows. The rest are on the Notifications page. */
export const BELL_SHOWS = 10;

/** The most held in memory; the store keeps thirty days, which in practice is far fewer. */
const HOLD = 500;

export type NotifyInput =
  | string
  | { level?: Level; title: string; body?: string; action?: NoteActionData; link?: NoteLinkData };

interface NotifyApi {
  notify: (input: NotifyInput) => void;
  notes: Note[];
  unread: number;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  /** Runs a notification's action, from its bubble, the bell or the Notifications page. */
  act: (id: string) => void;
  /** How each kind of action is called and run. */
  runners: Partial<ActionRunners>;
}

const NotifyContext = createContext<NotifyApi>({
  notify: () => {},
  notes: [],
  unread: 0,
  markAllRead: () => {},
  dismiss: () => {},
  act: () => {},
  runners: {},
});

const newId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    // Only where crypto is missing, which is no browser this runs in; ids stay unique enough.
    return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, "0").slice(-12)}`;
  }
};

const fromStored = (s: StoredNote): Note => ({
  id: s.id,
  level: s.level,
  title: s.title,
  ...(s.body ? { body: s.body } : {}),
  at: Date.parse(s.at),
  showing: false,
  read: s.read,
  ...(s.action ? { action: { data: s.action, state: s.actionDone ? "done" : "ready" } } : {}),
  ...(s.link ? { link: s.link } : {}),
});

export function NotifyProvider({
  children,
  store,
  runners = {},
}: {
  children: ReactNode;
  store?: NoteStore;
  runners?: Partial<ActionRunners>;
}) {
  const [notes, setNotes] = useState<Note[]>([]);
  const storeRef = useRef(store);
  storeRef.current = store;

  const update = useCallback((id: string, change: (note: Note) => Note) => {
    setNotes((all) => all.map((n) => (n.id === id ? change(n) : n)));
  }, []);

  // What is kept, merged with anything said in this tab that the store has not answered for yet.
  // Again whenever the window comes back into focus, so another device's notifications turn up.
  useEffect(() => {
    if (!store) return;
    let live = true;
    const load = () =>
      store.load().then(
        (kept) => {
          if (!live) return;
          setNotes((current) => {
            const here = new Map(current.map((n) => [n.id, n]));
            const merged = kept.map((k) => {
              const mine = here.get(k.id);
              here.delete(k.id);
              const note = fromStored(k);
              // A bubble still up, or an action mid-run, is this tab's to finish.
              return mine
                ? {
                    ...note,
                    showing: mine.showing,
                    ...(mine.action?.state === "running" ? { action: mine.action } : {}),
                  }
                : note;
            });
            return [...here.values(), ...merged].sort((a, b) => b.at - a.at).slice(0, HOLD);
          });
        },
        () => {},
      );
    load();
    window.addEventListener("focus", load);
    return () => {
      live = false;
      window.removeEventListener("focus", load);
    };
  }, [store]);

  const notify = useCallback(
    (input: NotifyInput) => {
      const given = typeof input === "string" ? { title: input } : input;
      const id = newId();
      const note: Note = {
        id,
        level: given.level ?? "info",
        title: given.title,
        ...(given.body ? { body: given.body } : {}),
        ...(given.action ? { action: { data: given.action, state: "ready" } } : {}),
        ...(given.link ? { link: given.link } : {}),
        at: Date.now(),
        showing: true,
        read: false,
      };
      setNotes((all) => [note, ...all].slice(0, HOLD));
      storeRef.current
        ?.add({
          id,
          level: note.level,
          title: note.title,
          ...(note.body ? { body: note.body } : {}),
          ...(given.action ? { action: given.action } : {}),
          ...(given.link ? { link: given.link } : {}),
        })
        .catch(() => {});
      // Out of the bubble, into the bell. It stays unread until someone opens the bell.
      setTimeout(() => update(id, (n) => ({ ...n, showing: false })), LINGER_MS);
    },
    [update],
  );

  const running = useRef(new Set<string>());
  const act = useCallback(
    (id: string) => {
      const note = notes.find((n) => n.id === id);
      const action = note?.action;
      const runner = action ? runners[action.data.kind] : undefined;
      // Never twice at once, and never once it has worked, from the bubble, the bell or the page.
      if (!action || !runner || action.state !== "ready" || running.current.has(id)) return;
      running.current.add(id);
      const setState = (state: ActionState) =>
        update(id, (n) => (n.action ? { ...n, action: { ...n.action, state } } : n));
      setState("running");
      runner.run(action.data).then(
        () => {
          running.current.delete(id);
          setState("done");
          storeRef.current?.actionDone(id).catch(() => {});
          // Its bubble has said its piece: what happened next is the next notification's to say.
          update(id, (n) => ({ ...n, showing: false }));
          if (runner.after) notify(runner.after);
        },
        (error: unknown) => {
          running.current.delete(id);
          setState("ready");
          notify({
            level: "warning",
            title: `${runner.label} did not go through`,
            body: error instanceof Error ? error.message : "Try it again in a moment.",
          });
        },
      );
    },
    [notes, runners, notify, update],
  );

  const markAllRead = useCallback(() => {
    setNotes((all) => all.map((n) => ({ ...n, read: true })));
    storeRef.current?.readAll().catch(() => {});
  }, []);

  const dismiss = useCallback(
    (id: string) => update(id, (n) => ({ ...n, showing: false })),
    [update],
  );

  const value = useMemo<NotifyApi>(
    () => ({
      notify,
      notes,
      // Only the ones that have gone into the bell. While a bubble is still up it is being read
      // right now, and counting it as well would say the same thing twice (Tyler, 2026-09-21).
      unread: notes.filter((n) => !n.read && !n.showing).length,
      markAllRead,
      dismiss,
      act,
      runners,
    }),
    [notify, notes, markAllRead, dismiss, act, runners],
  );

  return <NotifyContext.Provider value={value}>{children}</NotifyContext.Provider>;
}

/** Say something. A bare string is informational, which is most of them. */
export const useNotify = () => useContext(NotifyContext).notify;

/** The bell's own view of the list. */
export const useNotifications = () => useContext(NotifyContext);

/**
 * Says, as a page is left, that what was typed on it is kept and where it waits, with a link back
 * (Tyler, 2026-10-01: "Draft kept" takes you to the draft). `say` is read as the page goes, so it sees
 * the page as it was left, and returns null when there is nothing to say. Set the returned ref when
 * the page itself sends the person away (saved, thrown away, deleted, brewed): that leaves nothing
 * waiting, and saying otherwise would be untrue (Tyler, 2026-09-22).
 */
export function useKeptOnLeaving(say: () => NotifyInput | null) {
  const { notify } = useNotifications();
  const latest = useRef(say);
  latest.current = say;
  const handled = useRef(false);
  // Said a moment after the page goes, and not at all if it comes straight back: React's strict mode
  // in development unmounts every page once as it opens, which said "Draft kept" on arrival.
  const saying = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    clearTimeout(saying.current);
    return () => {
      if (handled.current) return;
      const said = latest.current();
      if (said) saying.current = setTimeout(() => notify(said), 0);
    };
  }, [notify]);
  return handled;
}
